import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useNavigate} from 'react-router';

import {RolesAccount} from '@/app/auth';
import {DEFAULT_CARD} from '@/app/main/onboarding/constants/OnboardingConstants';
import {useLoading} from '@/app/zustand/useLoading';
import {featureFlags} from '@/config/featureFlags';
import {C_ORIGIN} from '@/constants';
import {useAddKidRepository} from '@/dataSource/src/api/add-kid/useRepository';
import {useCardsRepository} from '@/dataSource/src/api/cards/useRepository';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError from '@/utils/errors/logError';

import {getKidData, saveKidData} from '../../services/handleKidInformation';

import {INTERVAL_TIME, MAX_ATTEMPTS} from './models';
import {useTrackers} from './trackers';

type ViewModelContextType = {
  selectedTheme: string;
  loaderText: LoadingTextType[];
};

type LoadingTextType = {
  text: string;
  completed: boolean;
};

interface IKidThemeSelected {
  user_id: string;
  card_slug: string;
}

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export const ViewModelProvider = ({children}: {children: ReactNode}) => {
  //#region [---- [PROPERTIES] ----]
  const navigate = useNavigate();
  const {profile, updateMetadata} = useUsersRepository();
  const kidData = getKidData();
  const {stopLoading, setSkipLoading} = useLoading();
  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;
  const {
    trackViewAddKidCreatingKidAccount,
    trackViewAddKidConfirmationError,
    trackViewCreateTeenWithTeenData,
  } = useTrackers();
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const isEnabledRequestPhysicalCard =
    featureFlags.isEnabledRequestPhysicalCard;
  //#endregion

  //#region [---- [REPOSITORIES] ----]
  const {
    hasErrorDontMatch,
    isNewKidSuccess,
    isNewKidError,
    kidCreated,
    handleNewKid,
  } = useAddKidRepository();
  const {cardsRef, handleGetCards} = useCardsRepository();
  //#endregion

  //#region [---- [HOOKS] ----]
  useEffect(() => {
    trackViewAddKidCreatingKidAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attemptsRef = useRef(0);
  const [loaderText, setCurrentText] = useState<LoadingTextType[]>([
    {
      text: `Creating <#801DE7;${kidData.firstName}'s> Modak account`,
      completed: false,
    },
  ]);

  const getMetadataKidsThemeSelected = useCallback((): IKidThemeSelected[] => {
    const rawMetadata = profile?.metadata?.ONBOARDING_CARD_KIDS_THEME_SELECTED;

    let previous: IKidThemeSelected[] = [];

    try {
      if (
        typeof rawMetadata === 'string' &&
        rawMetadata &&
        rawMetadata.length > 0
      ) {
        previous = JSON.parse(rawMetadata);
      } else if (Array.isArray(rawMetadata)) {
        previous = rawMetadata;
      }
    } catch (error) {
      logError(error, {
        customTitle: 'CreatingKid - Error fetching kids theme metadata',
        fileName: 'CreatingKid ViewModelContext',
        functionName: 'getMetadataKidsThemeSelected',
        data: rawMetadata,
      });
      console.warn(
        'Failed to parse ONBOARDING_CARD_KIDS_THEME_SELECTED metadata:',
        error,
      );
    }

    return previous;
  }, [profile?.metadata?.ONBOARDING_CARD_KIDS_THEME_SELECTED]);

  const updateMetadataKidsThemeSelected = useCallback(async () => {
    const newEntry: IKidThemeSelected = {
      user_id: kidCreated?.teen?.uuid,
      card_slug: selectedTheme,
    };

    if (!newEntry.user_id || !newEntry.card_slug) return;

    const previous = getMetadataKidsThemeSelected();

    const filtered = previous.filter(
      entry => entry.user_id !== newEntry.user_id,
    );
    const merged = [...filtered, newEntry];
    const jsonMetadata = JSON.stringify(merged);

    await updateMetadata({
      ONBOARDING_CARD_KIDS_THEME_SELECTED: jsonMetadata,
    });
  }, [
    getMetadataKidsThemeSelected,
    kidCreated?.teen?.uuid,
    selectedTheme,
    updateMetadata,
  ]);

  const isWebViewActive = useMemo(() => {
    return (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    );
  }, []);

  const handleSendMessageToWebView = useCallback(() => {
    stopLoading();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        status: WEB_VIEW_MESSAGES.CONTINUE_ONBOARDING,
        kidUuid: kidCreated?.teen?.uuid ?? kidData?.uuid,
        choseDesign: selectedTheme,
        previousScreen: 'addKidCreatingKid viewModelContext',
      }),
    );
  }, [kidCreated, kidData, selectedTheme, stopLoading]);

  useEffect(() => {
    setCurrentText([
      {
        text: `Creating <#801DE7;${kidData.firstName}'s> Modak account`,
        completed: false,
      },
    ]);
    if (!kidData.firstName || !kidData.lastName || !kidData.dob) {
      logError(new Error('Kid data is incomplete'), {
        customTitle: 'CreatingKid - Incomplete kid data',
        fileName: 'CreatingKid ViewModelContext',
        functionName: 'useEffect - kidData validation',
        data: kidData,
        severity: 'warning',
      });
      return;
    }
    handleNewKid({
      email: kidData?.email,
      phone: kidData?.phone,
      first_name: kidData.firstName,
      last_name: kidData.lastName,
      name: `${kidData.firstName} ${kidData.lastName}`,
      date_of_birth: kidData.dob,
      uuid: kidData?.uuid,
      parent: {
        phone: profile?.phone || '',
      },
      switch_account: kidData?.switchAccount || false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (kidCreated) {
      updateMetadataKidsThemeSelected();
      trackViewCreateTeenWithTeenData({
        type: RolesAccount.TEEN,
        email: kidCreated?.teen?.email,
        phone: kidCreated?.teen?.phone,
        uuid: kidCreated?.teen?.uuid,
      });
      saveKidData({uuid: kidCreated?.teen?.uuid});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidCreated]);

  useEffect(() => {
    if (isNewKidSuccess) {
      setCurrentText(prev => {
        prev.map(item => {
          item.completed = true;
          item.text = item.text.replace('801DE7', '646867');
        });
        return [
          ...prev,
          {
            text: `Setting up <#801DE7;${kidData.firstName}'s> account`,
            completed: false,
          },
        ];
      });
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewKidSuccess]);

  useEffect(() => {
    if (isNewKidError) {
      trackViewAddKidConfirmationError(C_ORIGIN.APIS_ERROR);
      stopPolling();
      navigate('/onboarding/add-kid/summary', {
        state: {showFamilyCodeBanner: hasErrorDontMatch},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewKidError]);

  //#endregion

  //#region [---- [LOGIC] ----]
  const stopPolling = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const askingStateProcessFetchCards = useCallback(async () => {
    try {
      await handleGetCards();
    } catch (error) {
      logError(error, {
        customTitle: 'CreatingKid - Error fetching cards during polling',
        fileName: 'CreatingKid ViewModelContext',
        functionName: 'askingStateProcessFetchCards',
      });
    }
  }, [handleGetCards]);

  const startPolling = useCallback(async () => {
    intervalIdRef.current = setInterval(async () => {
      if (attemptsRef.current < MAX_ATTEMPTS) {
        await askingStateProcessFetchCards();
        attemptsRef.current += 1;

        if (cardsRef.current?.length > 0) {
          setSkipLoading(false);
          stopPolling();
          if (isEnabledRequestPhysicalCard) {
            navigate('/onboarding/request-physical-card');
          } else {
            if (isWebViewActive) {
              handleSendMessageToWebView();
            } else {
              navigate('/onboarding/download-app');
            }
          }
        }
      } else {
        trackViewAddKidConfirmationError(C_ORIGIN.TIME_OUT_ERROR);
        stopPolling();
        logError(new Error('Max attempts reached'), {
          customTitle: 'CreatingKid - Max attempts reached fetching cards',
          fileName: 'CreatingKid ViewModelContext',
          functionName: 'startPolling',
          extraTags: {
            kid_uuid: kidCreated?.teen?.uuid,
          },
        });
        navigate('/onboarding/add-kid/warning');
      }
    }, INTERVAL_TIME);
  }, [
    askingStateProcessFetchCards,
    cardsRef,
    setSkipLoading,
    stopPolling,
    kidCreated,
    isEnabledRequestPhysicalCard,
    navigate,
    isWebViewActive,
    handleSendMessageToWebView,
    trackViewAddKidConfirmationError,
  ]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        selectedTheme,
        loaderText,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
};

export const useViewModelProvider = () => {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error('CreatingKid ViewModelProvider is not found.');
  }
  return context;
};
